import { skipToken } from '@apollo/client/react'
import IcOutlineMusicNote from '~icons/ic/outline-music-note'
import IcOutlinePlayCircle from '~icons/ic/outline-play-circle'
import { useGetMedia } from 'src/services/apollo/media'
import { useReferenceVideoThumbnailId } from 'src/services/orpc/media'
import {
  getImageThumbnailUrl,
  getThumbnailUrl,
  getThumbnailVariantUrl,
} from 'src/utils/media'
import type { MediaKind } from '../hooks'

export interface MediaMentionChipThumbProps {
  kind: MediaKind
  mediaId: string
  /**
   * When true, render the placeholder chrome regardless of whether a
   * thumbnail is available — used by the editor to flag broken refs without
   * triggering avoidable network fetches.
   */
  missing?: boolean
}

/**
 * Small thumbnail rendered inline on a `@mention` chip. Images / videos show
 * the actual media thumbnail (shares Apollo cache with `CompactTile`); audio
 * and missing refs fall back to a music-note icon.
 */
export function MediaMentionChipThumb({
  kind,
  mediaId,
  missing = false,
}: MediaMentionChipThumbProps) {
  const { data: media } = useGetMedia(
    kind === 'audio' || missing ? skipToken : mediaId,
  )
  /**
   * For videos we only trust the resolved `urls[THUMBNAIL]` variant; the
   * synthesized `media.thumbnailUrl` points at `/v1/media/<id>/thumbnail`
   * which 404s for non-image media, so letting it fall through would block
   * the oRPC fallback below and leave a broken-image glyph.
   */
  const mediaThumb =
    kind === 'audio' || missing
      ? undefined
      : kind === 'video'
        ? getThumbnailVariantUrl(media)
        : getThumbnailUrl(media)
  /**
   * Uploaded reference videos typically don't carry a THUMBNAIL variant on
   * the GraphQL media payload. Fall back to the oRPC generate-or-get-
   * thumbnail endpoint, which synthesises a still image from the video's
   * first frame and returns its image `mediaId`.
   */
  const { data: videoThumbId } = useReferenceVideoThumbnailId(
    kind === 'video' && !missing && !mediaThumb ? mediaId : undefined,
  )
  const thumb =
    mediaThumb ??
    (videoThumbId ? getImageThumbnailUrl(videoThumbId) : undefined)

  if (kind === 'audio') {
    return (
      <span
        aria-hidden
        className="inline-flex size-4 shrink-0 items-center justify-center rounded-[3px] bg-primary/20 text-primary"
      >
        <IcOutlineMusicNote className="size-3" />
      </span>
    )
  }

  return (
    <span
      aria-hidden
      className="inline-flex size-4 shrink-0 items-center justify-center overflow-hidden rounded-[3px] bg-neutral-200/70 text-neutral-500 dark:bg-neutral-700/70 dark:text-neutral-300"
    >
      {thumb ? (
        <img
          src={thumb}
          alt=""
          loading="lazy"
          decoding="async"
          className="block size-full object-cover"
        />
      ) : kind === 'video' ? (
        <IcOutlinePlayCircle className="size-3" />
      ) : null}
    </span>
  )
}
